import { FormField, fieldA11y } from '@/components/FormField';
import { Icon } from '@/components/Icon';
import { ProblemAlert } from '@/components/ProblemAlert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { BitbucketTokenGuide, TokenKindChoice } from './BitbucketTokenGuide';
import { TokenSource } from './TokenSource';
import { TokenVerify } from './TokenVerify';
import { namePlaceholder } from './scmConnectionModel';
import { useConnectScmForm, type ConnectScmForm as Form } from './useConnectScmForm';

/** What the token reaches: discovered, or read from a pasted URL (never typed slugs). */
function SourceField({ f }: { f: Form }) {
  return <TokenSource d={f.source} values={f.values} error={f.errorFor('repository') ?? f.errorFor('workspace')} stored={Boolean(f.stored)} />;
}

function NameField({ f }: { f: Form }) {
  const error = f.errorFor('name');
  return (
    <FormField id="scm-connection-name" label="Name" hint="How Hangar shows it. Default: Bitbucket · the workspace (and repository)" error={error}>
      <Input data-testid="scm-connection-name" value={f.values.name} onChange={(e) => f.set('name', e.target.value)} placeholder={namePlaceholder(f.values)} maxLength={120} autoComplete="off" {...fieldA11y('scm-connection-name', error, 'hint')} />
    </FormField>
  );
}

function TokenStored({ onReplace }: { onReplace: () => void }) {
  return (
    <div data-testid="scm-connection-token-stored" className="flex items-start gap-2 rounded-lg border border-succeeded/30 bg-succeeded/10 px-3 py-2 text-[13px] text-foreground">
      <Icon name="key" size={16} className="mt-0.5 shrink-0 text-succeeded" />
      <span className="min-w-0 flex-1">The token is stored; it is attached when you connect.</span>
      <Button type="button" size="sm" variant="ghost" className="-my-1" onClick={onReplace}>
        Use another token
      </Button>
    </div>
  );
}

function TokenField({ f }: { f: Form }) {
  const error = f.errorFor('token');
  if (f.stored) return <TokenStored onReplace={f.forgetToken} />;
  return (
    <div className="flex flex-col gap-2">
      <FormField id="scm-connection-token" label="Access token" required hint="Paste the token Bitbucket showed after Create. Connect verifies it with Bitbucket first; it is stored encrypted and never shown again." error={error}>
        <Input data-testid="scm-connection-token" type="password" value={f.values.token} onChange={(e) => f.set('token', e.target.value)} className="font-mono sm:text-[13px]" autoComplete="off" spellCheck={false} {...fieldA11y('scm-connection-token', error, 'hint')} />
      </FormField>
    </div>
  );
}

function ConnectFooter({ busy, onCancel, kind }: { busy: boolean; onCancel: () => void; kind: Form['values']['kind'] }) {
  return (
    <SheetFooter className="flex-row justify-end border-t">
      <Button type="button" variant="ghost" onClick={onCancel}>
        Cancel
      </Button>
      <Button type="submit" data-testid="scm-connection-submit" loading={busy}>
        <Icon name="account_tree" size={18} />
        Connect {kind === 'repository' ? 'repository' : 'workspace'}
      </Button>
    </SheetFooter>
  );
}

/** The sheet's content: the token kind, the guide to create the token, the token, what it reaches (discovered), the check and a name. */
export function ConnectForm({ onDone }: { onDone: () => void }) {
  const f = useConnectScmForm(onDone);
  return (
    <form data-testid="scm-connection-form" onSubmit={(e) => void f.onSubmit(e)} noValidate className="flex h-full min-h-0 flex-col" aria-label="Connect Bitbucket">
      <SheetHeader>
        <SheetTitle>Connect Bitbucket</SheetTitle>
        <SheetDescription id="scm-connection-desc" className="text-xs">
          Hangar reads your source with a Bitbucket access token and delivers it to your builds. Without Bitbucket Premium, connect each repository with its own repository access token.
        </SheetDescription>
      </SheetHeader>
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 sm:p-5">
        <TokenKindChoice kind={f.values.kind} onChange={f.setKind} />
        <BitbucketTokenGuide kind={f.values.kind} />
        <TokenField f={f} />
        <SourceField f={f} />
        <TokenVerify check={f.check} />
        <NameField f={f} />
        <ProblemAlert error={f.problem} testId="scm-connection-problem" />
      </div>
      <ConnectFooter busy={f.busy} onCancel={onDone} kind={f.values.kind} />
    </form>
  );
}
